import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CreateDrawer } from "@/components/common/CreateDrawer";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Skeletons } from "@/components/common/EmptyState";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Wallet, Plus, PaperPlaneTilt, PencilSimple, Trash, Check, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = [
  { name: "name", label: "Budget Name", required: true },
  { name: "brand", label: "Brand", type: "select" },
  { name: "category", label: "Category", type: "select", options: ["Advertising", "Influencers", "Photoshoots", "Events", "Content production", "PR"] },
  { name: "planned", label: "Planned Amount", type: "number", required: true },
];
const money = (v) => (v >= 100000 ? "₹" + (v / 100000).toFixed(1) + "L" : "₹" + ((v || 0) / 1000).toFixed(0) + "K");

export default function Budgets() {
  const { items, isLoading, create, update } = useResource("budgets");
  const { isApprover, user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [comment, setComment] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["budgets"] });
  const submitForApproval = async (b) => {
    try {
      const { data } = await api.post(`/budgets/${b.id}/submit`);
      invalidate();
      toast.success(data.email?.sent ? "Submitted — approval email sent" : "Submitted for approval (email pending — add Resend key)");
      if (detail) setDetail(data.budget);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit budget");
    }
  };
  const decide = async (b, action) => {
    const { data } = await api.post(`/budgets/${b.id}/decision`, { action, comment });
    invalidate(); setComment(""); setDetail(data);
    toast.success(`Budget ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "sent back for changes"}`);
  };
  const del = async (b) => { try { await api.delete(`/resource/budgets/${b.id}`); invalidate(); toast.success("Deleted"); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } setConfirm(null); };

  return (
    <div>
      <PageHeader title="Budget & Expenses" subtitle="Plan, submit and approve marketing spend" icon={Wallet}
        actions={<Button onClick={() => { setEditItem(null); setOpen(true); }} data-testid="add-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> New Budget</Button>} />

      {isLoading ? <Skeletons /> : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((b, i) => (
            <div key={b.id} data-testid="budgets-card" className="group rounded-2xl border border-border bg-surface-raised p-5" style={{ animation: `fade-up 0.4s ease-out ${i * 0.04}s both` }}>
              <div className="flex items-start justify-between">
                <div><p className="font-display text-lg font-bold">{b.name}</p><p className="text-sm text-muted-foreground">{b.brand} · {b.category}</p></div>
                <StatusBadge status={b.status} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                <div><p className="font-mono font-bold">{money(b.planned)}</p><p className="text-[10px] uppercase text-muted-foreground">Planned</p></div>
                <div><p className="font-mono font-bold text-teal-400">{money(b.approved)}</p><p className="text-[10px] uppercase text-muted-foreground">Approved</p></div>
                <div><p className="font-mono font-bold">{money(b.actual)}</p><p className="text-[10px] uppercase text-muted-foreground">Actual</p></div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDetail(b)} data-testid="budget-view">View / History</Button>
                {["Draft", "Under Review", "Rejected"].includes(b.status) && <Button size="sm" className="rounded-full bg-brand-blue text-white" onClick={() => submitForApproval(b)} data-testid="budget-submit"><PaperPlaneTilt size={14} className="mr-1" /> Submit</Button>}
                <div className="ml-auto flex opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => { setEditItem(b); setOpen(true); }} data-testid="edit-btn" className="rounded-lg p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
                  {isApprover && <button onClick={() => setConfirm(b)} data-testid="delete-btn" className="rounded-lg p-1.5 text-muted-foreground hover:text-brand-pink"><Trash size={15} /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="budget-drawer">
          {detail && (<>
            <SheetHeader><SheetTitle className="font-display text-xl">{detail.name}</SheetTitle></SheetHeader>
            <div className="mt-2"><StatusBadge status={detail.status} /></div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              {[["Planned", detail.planned], ["Approved", detail.approved], ["Committed", detail.committed], ["Actual", detail.actual]].map(([l, v]) => <div key={l} className="rounded-xl border border-border bg-surface-raised p-3"><p className="text-xs uppercase text-muted-foreground">{l}</p><p className="font-mono font-bold">{money(v)}</p></div>)}
            </div>

            {isApprover && ["Submitted", "Under Review"].includes(detail.status) && (
              <div className="mt-5 rounded-xl border border-border bg-surface-raised p-4">
                <p className="mb-2 text-sm font-semibold">Review decision</p>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" className="mb-3 rounded-xl" data-testid="decision-comment" />
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full bg-lime-500 text-black hover:bg-lime-400" onClick={() => decide(detail, "approve")} data-testid="budget-approve"><Check size={14} className="mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-brand-pink" onClick={() => decide(detail, "reject")} data-testid="budget-reject"><X size={14} className="mr-1" /> Reject</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(detail, "changes")} data-testid="budget-changes"><ArrowsClockwise size={14} className="mr-1" /> Changes</Button>
                </div>
              </div>
            )}

            <div className="mt-5">
              <p className="mb-2 text-sm font-semibold">Approval history & audit</p>
              <div className="space-y-3">
                {(detail.approval_history || []).map((h, idx) => (
                  <div key={idx} className="relative border-l-2 border-border pl-4">
                    <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-primary" />
                    <p className="text-sm font-medium">{h.action} · <span className="text-muted-foreground">{h.reviewer}</span></p>
                    {h.comment && <p className="text-xs text-muted-foreground">"{h.comment}"</p>}
                    <p className="text-[10px] text-muted-foreground">{new Date(h.at).toLocaleString()}</p>
                  </div>
                ))}
                {(detail.approval_history || []).length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
              </div>
            </div>
          </>)}
        </SheetContent>
      </Sheet>

      <CreateDrawer open={open} onOpenChange={setOpen} title="Budget" fields={FIELDS} mode={editItem ? "edit" : "create"} initial={editItem}
        submitting={create.isPending || update.isPending}
        onSubmit={(body) => editItem ? update.mutateAsync({ id: editItem.id, body }) : create.mutateAsync({ ...body, status: "Draft", approved: 0, committed: 0, actual: 0, revenue: 0, roi: 0, approval_history: [] })} />
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Delete this budget?" onConfirm={() => del(confirm)} />
    </div>
  );
}
